import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import LoginPromptModal from '../../components/LoginPromptModal';
import { useAuth } from '../../contexts/AuthContext';
import { playCorrect, playWrong, playClap } from '../../utils/sounds';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY    = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR      = 'linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%)';
const FONT       = "'Plus Jakarta Sans', sans-serif";

// ─── Animations ───────────────────────────────────────────────────────────────
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40%       { transform: scale(1.15) translateY(-6px); }
  70%       { transform: scale(1.07) translateY(-3px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50%       { transform: translateY(-12px) rotate(5deg); }
`;
const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50%       { transform: translateY(-8px) rotate(-3deg); }
`;
const bounceAnim = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  30%       { transform: translateY(-20px) scale(1.1); }
  60%       { transform: translateY(-10px) scale(1.05); }
`;
const shakeAnim = keyframes`
  0%, 100% { transform: translateX(0); }
  15%       { transform: translateX(-8px); }
  30%       { transform: translateX(8px); }
  45%       { transform: translateX(-6px); }
  60%       { transform: translateX(6px); }
`;
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(45,188,175,0.4); }
  50%       { box-shadow: 0 0 0 12px rgba(45,188,175,0); }
`;
const revealAnim = keyframes`
  from { filter: brightness(0) contrast(1); }
  to   { filter: brightness(1) contrast(1); }
`;

// ─── Animal Image Helper ──────────────────────────────────────────────────────
const IMG_VERSION = 'v2';
const EMOJI_TO_SLUG: Record<string, string> = {
  '🐘':'elephant','🦁':'lion','🐱':'cat','🐶':'dog','🐔':'chicken',
  '🐄':'cow','🐐':'goat','🦆':'duck','🐟':'fish','🐬':'dolphin',
  '🐋':'whale','🐢':'turtle','🐵':'monkey','🐯':'tiger','🐻':'bear',
  '🐼':'panda','🐨':'koala','🦒':'giraffe','🦓':'zebra','🦏':'rhino',
  '🐴':'horse','🐷':'pig','🐦':'bird','🦅':'eagle','🐧':'penguin',
  '🐍':'snake','🐊':'crocodile','🐸':'frog','🐰':'rabbit','🐭':'mouse',
  '🐹':'hamster','🦊':'fox','🦩':'flamingo','🐝':'bee','🐫':'camel','🦌':'deer',
};

// ─── Data ─────────────────────────────────────────────────────────────────────
interface Animal { nameId: string; emoji: string; }
const ANIMALS: Animal[] = [
  { nameId:'Gajah',      emoji:'🐘' }, { nameId:'Singa',    emoji:'🦁' },
  { nameId:'Kucing',     emoji:'🐱' }, { nameId:'Anjing',   emoji:'🐶' },
  { nameId:'Ayam',       emoji:'🐔' }, { nameId:'Sapi',     emoji:'🐄' },
  { nameId:'Bebek',      emoji:'🦆' }, { nameId:'Ikan',     emoji:'🐟' },
  { nameId:'Lumba-lumba',emoji:'🐬' }, { nameId:'Kura-kura',emoji:'🐢' },
  { nameId:'Monyet',     emoji:'🐵' }, { nameId:'Harimau',  emoji:'🐯' },
  { nameId:'Beruang',    emoji:'🐻' }, { nameId:'Panda',    emoji:'🐼' },
  { nameId:'Jerapah',    emoji:'🦒' }, { nameId:'Zebra',    emoji:'🦓' },
  { nameId:'Kuda',       emoji:'🐴' }, { nameId:'Burung',   emoji:'🐦' },
  { nameId:'Pinguin',    emoji:'🐧' }, { nameId:'Katak',    emoji:'🐸' },
  { nameId:'Kelinci',    emoji:'🐰' },
];
const MAX_ROUNDS = 10;
type OptionStatus = 'idle' | 'correct' | 'incorrect' | 'disabled';

// ─── Intro Styled Components ──────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR}; display: flex; flex-direction: column;
  align-items: center;
  overflow: hidden; font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px; display: flex; align-items: center;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; background: transparent; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #333; transition: color 0.2s;
  &:hover { color: ${PRIMARY}; }
  .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; min-height: 0;
`;
const FloatShadow = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,0.12); font-size: 24px;
  animation: ${p => (p.$delay ?? 0) % 2 === 0 ? float : floatDelay} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
`;
const CharCard = styled.div`
  width: min(60vw, 220px); aspect-ratio: 1;
  display: flex; align-items: center; justify-content: center;
  animation: ${bounce} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.15));
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease; font-family: ${FONT};
`;
const Handle = styled.div`width: 40px; height: 4px; background: #e0e0e0; border-radius: 99px; margin: 0 auto 18px;`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900; margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 20px;`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff; font-size: 17px; font-weight: 800;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4); font-family: ${FONT};
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

// ─── Game Styled Components ───────────────────────────────────────────────────
const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: ${BG_GR}; font-family: ${FONT};
`;
const GameNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.12);
  padding: 10px 16px; display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`font-size: 17px; font-weight: 800; color: #1a2a2a;`;
const ScoreBadge = styled.div`
  background: rgba(45,188,175,0.12); color: ${PRIMARY};
  padding: 6px 14px; border-radius: 10px; font-size: 14px; font-weight: 800;
`;
const GameBody = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; padding: 16px 16px 24px; gap: 16px;
  max-width: 520px; margin: 0 auto; width: 100%;
`;
const RoundInfo = styled.div`
  font-size: 13px; color: #7a8a8a; text-align: center; font-weight: 600;
`;

// Shadow question box — WHITE background for contrast
const ShadowBox = styled.div<{ $isDragOver: boolean; $result: 'correct' | 'incorrect' | null }>`
  background: #fff;
  border-radius: 28px;
  padding: 24px 40px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-width: 200px; min-height: 180px; width: 100%;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  border: 3px dashed ${p =>
    p.$result === 'correct' ? '#22c55e' :
    p.$result === 'incorrect' ? '#ef4444' :
    p.$isDragOver ? PRIMARY : 'rgba(45,188,175,0.25)'};
  background: ${p =>
    p.$result === 'correct' ? 'rgba(34,197,94,0.06)' :
    p.$result === 'incorrect' ? 'rgba(239,68,68,0.06)' :
    p.$isDragOver ? 'rgba(45,188,175,0.06)' : '#fff'};
  transition: all 0.2s;
  animation: ${p => p.$isDragOver ? css`${pulseGlow} 1s ease-in-out infinite` : 'none'};
  position: relative;
`;
const ShadowImg = styled.img<{ $revealed: boolean }>`
  width: min(140px, 36vw); height: min(140px, 36vw); object-fit: contain;
  filter: ${p => p.$revealed ? 'none' : 'brightness(0) contrast(1)'};
  animation: ${p => p.$revealed ? css`${revealAnim} 0.6s ease` : css`${fadeInScale} 0.4s ease`};
  display: block;
`;
const DropHint = styled.div`
  font-size: 12px; color: #94a3b8; font-weight: 600; margin-top: 8px;
  display: flex; align-items: center; gap: 4px;
`;
const ResultLabel = styled.div<{ $type: 'correct' | 'incorrect' }>`
  font-size: 15px; font-weight: 800; margin-top: 8px;
  color: ${p => p.$type === 'correct' ? '#16a34a' : '#dc2626'};
  animation: ${fadeInScale} 0.3s ease;
`;

const OptionsGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 12px; width: 100%;
`;

const OptionCard = styled.div<{ $status: OptionStatus; $isDragging: boolean }>`
  padding: 14px; border-radius: 20px; border: 3px solid;
  border-color: ${p => p.$status === 'correct' ? '#22c55e' : p.$status === 'incorrect' ? '#ef4444' : p.$isDragging ? PRIMARY : '#e2e8f0'};
  background: ${p => p.$status === 'correct' ? 'rgba(34,197,94,0.08)' : p.$status === 'incorrect' ? 'rgba(239,68,68,0.08)' : p.$isDragging ? 'rgba(45,188,175,0.06)' : '#fff'};
  cursor: ${p => p.$status === 'idle' ? 'grab' : 'default'};
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  min-height: 110px; justify-content: center;
  transition: all 0.2s; box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  opacity: ${p => p.$status === 'disabled' ? 0.4 : p.$isDragging ? 0.5 : 1};
  transform: ${p => p.$isDragging ? 'scale(0.96)' : 'scale(1)'};
  animation: ${p =>
    p.$status === 'correct' ? css`${bounceAnim} 0.6s ease` :
    p.$status === 'incorrect' ? css`${shakeAnim} 0.5s ease` : 'none'};
  user-select: none; -webkit-user-select: none;
  touch-action: none;
  &:hover { border-color: ${p => p.$status === 'idle' ? PRIMARY : undefined}; }
  &:active { cursor: grabbing; }
`;
const OptionLabel = styled.div<{ $status: OptionStatus }>`
  font-size: 12px; font-weight: 700;
  color: ${p => p.$status === 'correct' ? '#16a34a' : p.$status === 'incorrect' ? '#dc2626' : '#94a3b8'};
`;
const DragGhost = styled.div`
  position: fixed; pointer-events: none; z-index: 9999;
  border-radius: 16px; background: rgba(255,255,255,0.95);
  border: 3px solid ${PRIMARY}; padding: 8px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
  transform: translate(-50%, -50%);
  transition: none;
`;
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
`;
const Modal = styled.div`
  background: #fff; border-radius: 28px; padding: 28px 24px 24px;
  text-align: center; max-width: 300px; width: 88%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  animation: ${fadeInScale} 0.4s cubic-bezier(0.175,0.885,0.32,1.275);
  font-family: ${FONT};
`;
const ModalEmoji = styled.div`font-size: 52px; margin-bottom: 10px;`;
const ModalTitle = styled.div`font-size: 24px; font-weight: 900; color: #1a2a2a; margin-bottom: 6px;`;
const ModalDesc = styled.div`font-size: 14px; color: #7a8a8a; margin-bottom: 20px; line-height: 1.5;`;
const ModalBtnRow = styled.div`display: flex; gap: 8px; justify-content: center;`;
const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 11px 22px; border-radius: 99px;
  border: ${p => p.$primary ? 'none' : '2px solid #e0e0e0'};
  background: ${p => p.$primary ? PRIMARY_GR : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#555'};
  font-size: 14px; font-weight: 700; cursor: pointer; font-family: ${FONT};
  box-shadow: ${p => p.$primary ? '0 4px 16px rgba(43,188,175,0.4)' : 'none'};
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); }
`;

// Sound aliases for this game
const playYeay  = playCorrect;
const playTetot = playWrong;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ShadowGame() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [showIntro, setShowIntro]         = useState(true);
  const [questions, setQuestions]         = useState<Animal[]>([]);
  const [current, setCurrent]             = useState<Animal | null>(null);
  const [options, setOptions]             = useState<Animal[]>([]);
  const [score, setScore]                 = useState(0);
  const [round, setRound]                 = useState(1);
  const [feedback, setFeedback]           = useState<Record<string, OptionStatus>>({});
  const [answered, setAnswered]           = useState(false);
  const [showModal, setShowModal]         = useState(false);
  const [revealed, setRevealed]           = useState(false);
  const [dropResult, setDropResult]       = useState<'correct' | 'incorrect' | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const loginShownRef = useRef(false);

  // Drag state
  const [draggingEmoji, setDraggingEmoji] = useState<string | null>(null);
  const [isDragOver, setIsDragOver]       = useState(false);
  const [ghostPos, setGhostPos]           = useState({ x: 0, y: 0 });
  const [showGhost, setShowGhost]         = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ emoji: string; x: number; y: number } | null>(null);

  const generateRound = useCallback((q: Animal[], r: number) => {
    setFeedback({}); setAnswered(false); setRevealed(false); setDropResult(null);
    const correct = q[r - 1];
    const wrong = ANIMALS.filter(a => a.emoji !== correct.emoji).sort(() => Math.random() - 0.5).slice(0, 3);
    setCurrent(correct);
    setOptions([correct, ...wrong].sort(() => Math.random() - 0.5));
    // Show login prompt at round 2 if not logged in
    if (r >= 2 && !loginShownRef.current) {
      // Check auth after a short delay to let authLoading settle
      setTimeout(() => {
        if (!loginShownRef.current) {
          loginShownRef.current = true;
          setShowLoginPrompt(true);
        }
      }, 400);
    }
  }, []);

  const startGame = useCallback(() => {
    const q = [...ANIMALS].sort(() => Math.random() - 0.5).slice(0, MAX_ROUNDS);
    loginShownRef.current = !!user; // reset: don't show again if already logged in
    setQuestions(q); setScore(0); setRound(1); setShowModal(false);
    generateRound(q, 1);
  }, [generateRound, user]);

  const handleAnswer = useCallback((selectedEmoji: string) => {
    if (answered || !current) return;
    setAnswered(true);

    if (selectedEmoji === current.emoji) {
      setFeedback({ [selectedEmoji]: 'correct' });
      setDropResult('correct');
      setRevealed(true);
      playYeay();
      setScore(s => s + 10);
      setTimeout(() => {
        if (round >= MAX_ROUNDS) { setShowModal(true); playClap(); }
        else { const nr = round + 1; setRound(nr); generateRound(questions, nr); }
      }, 1400);
    } else {
      const fb: Record<string, OptionStatus> = {};
      options.forEach(o => {
        if (o.emoji === selectedEmoji) fb[o.emoji] = 'incorrect';
        else if (o.emoji === current.emoji) fb[o.emoji] = 'correct';
        else fb[o.emoji] = 'disabled';
      });
      setFeedback(fb);
      setDropResult('incorrect');
      setRevealed(true);
      playTetot();
      setTimeout(() => {
        if (round >= MAX_ROUNDS) setShowModal(true);
        else { const nr = round + 1; setRound(nr); generateRound(questions, nr); }
      }, 1600);
    }
  }, [answered, current, options, round, questions, generateRound]);

  // ── HTML5 Drag & Drop (desktop) ───────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, emoji: string) => {
    if (answered) return;
    e.dataTransfer.setData('emoji', emoji);
    setDraggingEmoji(emoji);
  };
  const handleDragEnd = () => setDraggingEmoji(null);
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false); setDraggingEmoji(null);
    const emoji = e.dataTransfer.getData('emoji');
    if (emoji) handleAnswer(emoji);
  };

  // ── Touch Drag (mobile) ───────────────────────────────────────────────────
  const handleTouchStart = (e: React.TouchEvent, emoji: string) => {
    if (answered) return;
    const t = e.touches[0];
    touchStartRef.current = { emoji, x: t.clientX, y: t.clientY };
    setDraggingEmoji(emoji);
    setGhostPos({ x: t.clientX, y: t.clientY });
    setShowGhost(true);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    e.preventDefault();
    const t = e.touches[0];
    setGhostPos({ x: t.clientX, y: t.clientY });
    // Check if over drop zone
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const dropZone = dropZoneRef.current;
    setIsDragOver(!!(dropZone && (dropZone === el || dropZone.contains(el))));
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const t = e.changedTouches[0];
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const dropZone = dropZoneRef.current;
    const wasOverDrop = !!(dropZone && (dropZone === el || dropZone.contains(el)));
    setShowGhost(false); setIsDragOver(false); setDraggingEmoji(null);
    if (wasOverDrop) handleAnswer(touchStartRef.current.emoji);
    touchStartRef.current = null;
  };

  const getStatus = (emoji: string): OptionStatus => {
    if (feedback[emoji]) return feedback[emoji];
    if (answered) return 'disabled';
    return 'idle';
  };

  if (showIntro) {
    return (
      <IntroOverlay>
        <IntroWrapper>
        <TopNav><BackBtn onClick={() => navigate('/')}><span className="material-symbols-outlined">arrow_back</span></BackBtn></TopNav>
        <CharArea>
          <FloatShadow $top="16%" $left="8%" $delay={0}>
            <img src={`/assets/images/animals/animal_elephant.png?${IMG_VERSION}`} alt="" style={{ width: 28, height: 28, objectFit: 'contain', filter: 'brightness(0) contrast(1)' }} />
          </FloatShadow>
          <FloatShadow $top="12%" $right="10%" $delay={1}>
            <img src={`/assets/images/animals/animal_lion.png?${IMG_VERSION}`} alt="" style={{ width: 28, height: 28, objectFit: 'contain', filter: 'brightness(0) contrast(1)' }} />
          </FloatShadow>
          <FloatShadow $bottom="22%" $left="10%" $delay={2}>
            <img src={`/assets/images/animals/animal_giraffe.png?${IMG_VERSION}`} alt="" style={{ width: 28, height: 28, objectFit: 'contain', filter: 'brightness(0) contrast(1)' }} />
          </FloatShadow>
          <FloatShadow $bottom="18%" $right="8%" $delay={3}>
            <img src={`/assets/images/animals/animal_penguin.png?${IMG_VERSION}`} alt="" style={{ width: 28, height: 28, objectFit: 'contain', filter: 'brightness(0) contrast(1)' }} />
          </FloatShadow>
          <CharCard>
            <img src={`/assets/images/animals/animal_elephant.png?${IMG_VERSION}`} alt="shadow" style={{ width: '80%', height: '80%', objectFit: 'contain', filter: 'brightness(0) contrast(1)' }} />
          </CharCard>
        </CharArea>
        <InfoCard>
          <Handle />
          <IntroTitle>Tebak <span>Bayangan!</span></IntroTitle>
          <IntroDesc>Lihat bayangan hewan lalu <strong>seret jawaban</strong> ke kotak bayangan, atau ketuk pilihan yang benar! 🕵️</IntroDesc>
          <StartBtn onClick={() => { setShowIntro(false); startGame(); }}>Mulai Bermain</StartBtn>
        </InfoCard>
        </IntroWrapper>
      </IntroOverlay>
    );
  }

  const currentSlug = current ? EMOJI_TO_SLUG[current.emoji] : null;

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => setShowIntro(true)}><span className="material-symbols-outlined">arrow_back</span></BackBtn>
          <GameTitle>Tebak Bayangan</GameTitle>
        </NavLeft>
        <ScoreBadge>Skor: {score}</ScoreBadge>
      </GameNav>

      <GameBody>
        <RoundInfo>Ronde {round} / {MAX_ROUNDS}</RoundInfo>

        {/* Drop Zone — white background */}
        <ShadowBox
          ref={dropZoneRef}
          key={round}
          $isDragOver={isDragOver}
          $result={dropResult}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {currentSlug && (
            <ShadowImg
              src={`/assets/images/animals/animal_${currentSlug}.png?${IMG_VERSION}`}
              alt="shadow"
              $revealed={revealed}
            />
          )}
          {dropResult === 'correct' && <ResultLabel $type="correct">✅ {current?.nameId}!</ResultLabel>}
          {dropResult === 'incorrect' && <ResultLabel $type="incorrect">❌ Bukan! Ini {current?.nameId}</ResultLabel>}
          {!answered && (
            <DropHint>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>drag_indicator</span>
              Seret & lepas jawaban di sini
            </DropHint>
          )}
        </ShadowBox>

        {/* Answer options */}
        <OptionsGrid key={`options-${round}`}>
          {options.map(animal => {
            const status = getStatus(animal.emoji);
            const slug = EMOJI_TO_SLUG[animal.emoji];
            const isDragging = draggingEmoji === animal.emoji;
            return (
              <OptionCard
                key={animal.emoji}
                $status={status}
                $isDragging={isDragging}
                draggable={status === 'idle'}
                onDragStart={e => handleDragStart(e, animal.emoji)}
                onDragEnd={handleDragEnd}
                onTouchStart={e => handleTouchStart(e, animal.emoji)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {slug
                  ? <img src={`/assets/images/animals/animal_${slug}.png?${IMG_VERSION}`} alt={animal.emoji}
                      style={{ width: 72, height: 72, objectFit: 'contain', pointerEvents: 'none' }} />
                  : <span style={{ fontSize: 56, pointerEvents: 'none' }}>{animal.emoji}</span>}
                {(status === 'correct' || status === 'incorrect') && (
                  <OptionLabel $status={status}>
                    {status === 'correct' ? `✓ ${animal.nameId}` : '✗ Bukan!'}
                  </OptionLabel>
                )}
              </OptionCard>
            );
          })}
        </OptionsGrid>
      </GameBody>

      {/* Drag Ghost for touch */}
      {showGhost && draggingEmoji && (
        <DragGhost style={{ left: ghostPos.x, top: ghostPos.y }}>
          {EMOJI_TO_SLUG[draggingEmoji]
            ? <img src={`/assets/images/animals/animal_${EMOJI_TO_SLUG[draggingEmoji]}.png?${IMG_VERSION}`}
                alt="" style={{ width: 64, height: 64, objectFit: 'contain', display: 'block' }} />
            : <span style={{ fontSize: 52 }}>{draggingEmoji}</span>}
        </DragGhost>
      )}

      {showLoginPrompt && !user && !authLoading && (
        <LoginPromptModal
          
          onClose={() => setShowLoginPrompt(false)}
          subtitle="Silahkan login terlebih dahulu untuk melanjutkan permainan."
          required
        />
      )}

      {showModal && (
        <ModalOverlay>
          <Modal>
            <ModalEmoji>{score >= MAX_ROUNDS * 8 ? '🏆' : score >= MAX_ROUNDS * 5 ? '🌟' : '💪'}</ModalEmoji>
            <ModalTitle>{score >= MAX_ROUNDS * 8 ? 'Sempurna!' : score >= MAX_ROUNDS * 5 ? 'Hebat!' : 'Terus Berlatih!'}</ModalTitle>
            <ModalDesc>Skor akhir: <strong>{score}</strong> dari {MAX_ROUNDS * 10}</ModalDesc>
            <ModalBtnRow>
              <ModalBtn onClick={() => navigate('/games')}>Menu</ModalBtn>
              <ModalBtn $primary onClick={startGame}>Main Lagi</ModalBtn>
            </ModalBtnRow>
          </Modal>
        </ModalOverlay>
      )}
    </GameWrap>
  );
}
